import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { INPUT_TYPES_MAP } from '@constants/input-types-map';
import { UserModel } from '@data-layer/models/user';
import { ReplaySubject } from 'rxjs';
import { CompaniesService } from '@services/companies.service';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { VerificationCodeHelper } from '@helpers/verification-code.helper';
import { IErrorForm } from '@shared/form-wrapper/interfaces';

@Component({
  selector: 'verify-phone-number-dashboard',
  templateUrl: './verify-phone-number-dashboard.component.html',
  styleUrls: ['./verify-phone-number-dashboard.component.scss'],
})
export class VerifyPhoneNumberDashboardComponent implements OnInit {
  @Output() public verified: EventEmitter<void> = new EventEmitter();

  public isFillingFirstStep: boolean = true;

  public userForm: UntypedFormGroup;
  public verifyCompleted: boolean = false;
  public formattedPhoneNumber: string;
  public code: any[] = [];
  public invalidCode: boolean = false;
  public integerInputMap: { mask: string; pattern: string } = INPUT_TYPES_MAP.INTEGER;
  public isOpenModal: boolean = false;
  public errors: { [name: string]: IErrorForm };

  private user: UserModel;

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject();

  constructor(private companiesService: CompaniesService, private fb: UntypedFormBuilder) {}

  public ngOnInit(): void {
    this.userForm = this.fb.group({
      phoneNumber: [null, [Validators.required, Validators.maxLength(15)]],
    });
    this.userForm
      .get(['phoneNumber'])
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe((data: any) => {
        if (data) {
          this.formattedPhoneNumber = data;
        } else {
          this.formattedPhoneNumber = undefined;
        }
      });
    this.createFormsErrors();
  }

  public openModal(user: UserModel): void {
    this.user = user;
    this.userForm.get('phoneNumber').patchValue(user.phoneNumber);

    this.isOpenModal = true;
  }

  public onConfirm(): void {
    this.isOpenModal = false;
  }

  public onDecline(): void {
    this.isOpenModal = false;
    this.isFillingFirstStep = true;
  }

  public resend(e: Event): void {
    e.preventDefault();
    this.companiesService.phoneConfirmation(this.formattedPhoneNumber).pipe(take(1)).subscribe();
  }

  public onKey(e: any): void {
    const { id } = e.target;
    const value = e.key;
    if (value.match(/^\d+\.?\d*$/)) {
      this.code[id] = value;
    }
    if (e.keyCode === 8) {
      this.code[id] = '';
    }
  }

  public get isVerificationCodeExpired(): boolean {
    if (this.userForm.get('phoneNumber').invalid || !this.userForm.get('phoneNumber').value) {
      return true;
    }
    return VerificationCodeHelper.isVerificationCodeExpired(this.formattedPhoneNumber);
  }

  public getVerificationCode(): void {
    if (!this.userForm.get('phoneNumber').valid) {
      return;
    }
    this.updatePhoneNumber();
    if (this.isVerificationCodeExpired) {
      this.companiesService
        .phoneConfirmation(this.formattedPhoneNumber)
        .pipe(takeUntil(this.isDestroy))
        .subscribe(() => {
          VerificationCodeHelper.setVerificationCode(this.formattedPhoneNumber);
        });
    }
    this.isFillingFirstStep = false;
  }

  public verifyPhone(): void {
    this.companiesService
      .confirmationPhoneCode(this.formattedPhoneNumber, this.code.join(''))
      .pipe(take(1))
      .subscribe((res: { valid: boolean }) => {
        if (res.valid) {
          this.verifyCompleted = true;
          this.user.phoneNumberConfirmed = true;
          this.companiesService
            .getUserData(true)
            .pipe(take(1))
            .subscribe(() => {
              this.verified.emit();
            });
        } else {
          this.invalidCode = true;
        }
      });
  }

  private updatePhoneNumber(): void {
    if (this.formattedPhoneNumber === this.companiesService.getUser().phoneNumber) {
      return;
    }
    this.companiesService
      .updateUser({ ...this.companiesService.getUser(), phoneNumber: this.formattedPhoneNumber })
      .pipe(
        switchMap(() => this.companiesService.getUserData()),
        takeUntil(this.isDestroy),
      )
      .subscribe();
  }

  private createFormsErrors(): void {
    this.errors = {
      phoneNumber: {
        maxlength: 'FORM_ERROR.MAX_LENGTH',
        validatePhoneNumber: 'FORM_ERROR.PHONE_NUMBER',
      },
    };
  }
}
